import { useEffect, useRef } from "react";
import {
  DataResult,
  State,
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from "@progress/kendo-data-query";
import axios from "axios";
import { createPortal } from "react-dom";

interface GridDataLoaderProps {
  readonly url: string;
  readonly dataState: State;
  readonly onDataReceived: (dataResult: DataResult) => void;
}

export function GridDataLoader({ url, dataState, onDataReceived }: GridDataLoaderProps) {
  const lastSuccess = useRef<string>("");
  const pending = useRef<string>("");

  const requestDataIfNeeded = () => {
    const dataStateAsRequestString = toDataSourceRequestString(dataState);

    // Check if the request parameters have changed
    if (pending.current || dataStateAsRequestString === lastSuccess.current) {
      return;
    }

    const { group } = dataState;
    const hasGroups = group && group.length;

    pending.current = dataStateAsRequestString;
    axios
      .request({
        method: "POST",
        url,
        data: dataStateAsRequestString,
      })
      .then((response) => response.data)
      .then((x) => {
        lastSuccess.current = pending.current;
        pending.current = "";
        if (toDataSourceRequestString(dataState) === lastSuccess.current) {
          onDataReceived.call(undefined, {
            data: hasGroups ? translateDataSourceResultGroups(x.Data) : x.Data,
            total: x.Total,
          });
        } else {
          requestDataIfNeeded();
        }
      });
  };

  useEffect(() => {
    requestDataIfNeeded();
  });

  return pending.current ? <LoadingPanel /> : null;
}

function LoadingPanel() {
  const loadingPanel = (
    <div className="k-loading-mask">
      <span className="k-loading-text">Loading</span>
      <div className="k-loading-image" />
      <div className="k-loading-color" />
    </div>
  );

  const gridContent = document && document.querySelector(".k-grid-content");
  return gridContent ? createPortal(loadingPanel, gridContent) : loadingPanel;
}
